<template>
  <div class="profile-container">
    <div class="profile-form">
      <form @submit.prevent="updateProfile">
        <p class="section-heading">Employee Profile</p>
        <div class="form-row">
          <div class="form-group">
            <label for="company">Company</label>
            <input id="company" type="text" class="form-control" disabled :value="companyName">
          </div>
          <div class="form-group">
            <label for="fullName">Full Name as per NIC <span class="required">*</span></label>
            <input id="fullName" type="text" class="form-control" placeholder="Enter your full name" v-model="user.fullName"
              :class="{'is-invalid': errors.fullName}" aria-required="true" aria-describedby="fullNameError">
            <div v-if="errors.fullName" id="fullNameError" class="invalid-feedback">
              {{ errors.fullName }}
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="designation">Designation <span class="required">*</span></label>
            <div :class="{'is-invalid': errors.designation}">
            <CustomDropdown id="designation" label="" :options="designations" :value="user.designation"
              placeholder="Select your designation" :show-error="false" @change="onDesignationChange"
              :disabled="isNonAdmin" :aria-required="!isNonAdmin" />
            </div>
            <div v-if="errors.designation" id="designationError" class="invalid-feedback">
              {{ errors.designation }}
            </div>
          </div>
          <div class="form-group">
            <label for="joiningDate">Joining Date <span class="required">*</span></label>
            <input id="joiningDate" type="date" class="form-control" v-model="user.joiningDate"
              :disabled="isNonAdmin" :aria-required="!isNonAdmin" :class="{'is-invalid': errors.joiningDate}">
            <div v-if="errors.joiningDate" id="joiningDateError" class="invalid-feedback">
              {{ errors.joiningDate }}
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="grade">Grade <span class="required">*</span></label>
            <div :class="{'is-invalid': errors.grade}">
            <GradeDropdown id="grade" v-model="user.grade" :designation="user.designation" :designationGrades="designationGrades"
              placeholder="Select Grade" :show-error="errors.grade !== undefined" :error-message="errors.grade" :disabled="isNonAdmin" @change="onGradeChange" />
            </div>
          </div>
          <div class="form-group">
            <label for="companyEmail">Company Email <span class="required">*</span></label>
            <input id="companyEmail" type="email" class="form-control" placeholder="employee@expertizo.pk" v-model="user.email"
              :disabled="isNonAdmin" :aria-required="!isNonAdmin" :class="{'is-invalid': errors.companyEmail}">
            <div v-if="errors.companyEmail" id="companyEmailError" class="invalid-feedback">
              {{ errors.companyEmail }}
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="personalEmail">Personal Email <span class="required">*</span></label>
            <input id="personalEmail" type="email" class="form-control" placeholder="Enter your personal email"
              v-model="user.personalEmail" :class="{'is-invalid': errors.personalEmail}" aria-required="true" aria-describedby="personalEmailError">
            <div v-if="errors.personalEmail" id="personalEmailError" class="invalid-feedback">
              {{ errors.personalEmail }}
            </div>
          </div>
          <div class="form-group">
            <label for="nicNumber">NIC Number <span class="required">*</span></label>
            <input id="nicNumber" type="text" class="form-control" placeholder="00000-0000000-0" v-model="user.nicNumber"
              :class="{'is-invalid': errors.nicNumber}" aria-required="true" aria-describedby="nicNumberError">
            <div v-if="errors.nicNumber" id="nicNumberError" class="invalid-feedback">
              {{ errors.nicNumber }}
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="dateOfBirth">Date of birth <span class="required">*</span></label>
            <input id="dateOfBirth" type="date" class="form-control" v-model="user.dateOfBirth"
              :class="{'is-invalid': errors.dateOfBirth}" aria-required="true" aria-describedby="dateOfBirthError">
            <div v-if="errors.dateOfBirth" id="dateOfBirthError" class="invalid-feedback">
              {{ errors.dateOfBirth }}
            </div>
          </div>
          <div class="form-group">
            <label for="bankName">Bank Name <span class="required">*</span></label>
            <div class="bank-field">
            <CustomDropdown id="bankName" label="" :options="banks" :value="user.bankName"
              placeholder="Select your bank" :show-error="errors.bankName !== undefined"
              :error-message="errors.bankName" @change="onBankChange" :custom="true" />
              <input id="bankName" v-if="showCustomBank" ref="customBank" v-model="customBankName" type="text" placeholder="Enter bank name" class="form-control custom-bank-input" @input="onCustomBankInput" />
              </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="iban">IBAN/Account no <span class="required">*</span></label>
            <input id="iban" type="text" class="form-control" placeholder="Enter your IBAN or account number" v-model="user.iban"
              :class="{'is-invalid': errors.iban}" aria-required="true" aria-describedby="ibanError">
            <div v-if="errors.iban" id="ibanError" class="invalid-feedback">
              {{ errors.iban }}
            </div>
          </div>
          <div class="form-group">
            <label for="nationality">Nationality <span class="required">*</span></label>
            <CustomDropdown id="nationality" label="" :options="countries" :value="user.nationality"
              placeholder="Select your nationality" :show-error="errors.nationality !== undefined"
              :error-message="errors.nationality" @change="onNationalityChange" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="maritalStatus">Marital Status <span class="required">*</span></label>
            <CustomDropdown id="maritalStatus" label="" :options="maritalStatusOptions" :value="user.maritalStatus"
              placeholder="Select your marital status" :show-error="errors.maritalStatus !== undefined"
              :error-message="errors.maritalStatus" @change="onMaritalStatusChange" />
          </div>
          <div class="form-group">
            <label for="currentAddress">Current Address <span class="required">*</span></label>
            <input id="currentAddress" type="text" class="form-control" placeholder="Enter your current address"
              v-model="user.currentAddress" :class="{'is-invalid': errors.currentAddress}" aria-required="true" aria-describedby="currentAddressError">
            <div v-if="errors.currentAddress" id="currentAddressError" class="invalid-feedback">
              {{ errors.currentAddress }}
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="permanentAddress">Permanent Address <span class="required">*</span></label>
            <input id="permanentAddress" type="text" class="form-control" placeholder="Enter your permanent address"
              v-model="user.permanentAddress" :class="{'is-invalid': errors.permanentAddress}" aria-required="true" aria-describedby="permanentAddressError">
            <div v-if="errors.permanentAddress" id="permanentAddressError" class="invalid-feedback">
              {{ errors.permanentAddress }}
            </div>
          </div>
          <div class="form-group">
            <label for="contactNumber">Contact Number <span class="required">*</span></label>
            <input id="contactNumber" type="number" class="form-control" placeholder="Enter your contact number" v-model="user.phoneNumber"
              :class="{'is-invalid': errors.contactNumber}" aria-required="true" aria-describedby="contactNumberError">
            <div v-if="errors.contactNumber" id="contactNumberError" class="invalid-feedback">
              {{ errors.contactNumber }}
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="emergencyContactName">Emergency Contact Name & Relationship <span class="required">*</span></label>
            <input id="emergencyContactName" type="text" class="form-control" placeholder="E.g., John Doe (Father)"
              v-model="user.emergencyContactName" :class="{'is-invalid': errors.emergencyContactName}" aria-required="true" aria-describedby="emergencyContactNameError">
            <div v-if="errors.emergencyContactName" id="emergencyContactNameError" class="invalid-feedback">
              {{ errors.emergencyContactName }}
            </div>
          </div>
          <div class="form-group">
            <label for="emergencyContactNumber">Emergency Contact Number <span class="required">*</span></label>
            <input id="emergencyContactNumber" type="number" class="form-control" placeholder="Enter emergency contact number"
              v-model="user.emergencyContactNumber" :class="{'is-invalid': errors.emergencyContactNumber}" aria-required="true" aria-describedby="emergencyContactNumberError">
            <div v-if="errors.emergencyContactNumber" id="emergencyContactNumberError" class="invalid-feedback">
              {{ errors.emergencyContactNumber }}
            </div>
          </div>
        </div>
        <div class="">
          <label for="nicImage">NIC Picture <span class="required">*</span></label>
          <div v-if="!nicPreviewUrl" class="file-upload-area" @click="triggerNicFileInput" @dragover.prevent @drop.prevent="handleNicFileDrop">
            <input id="nicImage" type="file" ref="nicImageInput" @change="handleNicImageUpload" accept=".pdf,image/*" class="file-input" />
            <div class="upload-icon">
              <i class="fas fa-upload"></i>
            </div>
            <div class="upload-text">
              <span v-if="errors.nicImageUrl" class="invalid-feedback" style="margin-top: -12px;">This field is required</span>
              Only Click to browse or drag and drop
              <div class="upload-format">Supported formats: IMAGE, PDF</div>
            </div>
          </div>
          <div v-else class="file-preview">
            <div class="file-info">
              <img v-if="nicPreviewIsImage" :src="nicPreviewUrl" alt="NIC Image" class="preview-image" />
              <img v-else-if="nicPreviewIsPdf" :src="getDocumentThumbnail(nicPreviewUrl)" alt="Document Preview" class="preview-image" />
              <i v-else class="fas fa-file-word fa-3x" style="color: #2b579a;"></i>
            </div>
            <div class="action-button-container">
              <button type="button" class="view-btn" @click="openFileInNewTab(nicPreviewUrl)">
                <i class="fas fa-eye"></i>
              </button>
              <button type="button" class="replace-btn" @click="triggerNicFileInput" title="Replace NIC">
                <i class="fas fa-sync-alt"></i>
              </button>
            </div>
            <input id="nicImageReplace" type="file" ref="nicImageInput" @change="handleNicImageUpload" accept=".pdf,image/*" class="file-input" style="display:none;" />
          </div>
        </div>
        <div class="submit-button-container" >
          <button type="submit" class="button-submit" style="min-width: 150px">
            <span v-if="profileLoading">
              <img src="/static/img/loader.gif" alt="Loading" style="width:18px; height:18px;vertical-align:middle;" />
            </span>
            <span v-else>
              Submit Profile
            </span>
          </button>
        </div>
      </form>

      <VerifyDocuments
        :user="user"
        :isUploadingExperienceLetter="isUploadingExperienceLetter"
        :isUploadingSalarySlip="isUploadingSalarySlip"
        :experienceLetterFileName="experienceLetterFileName"
        :experienceLetterFileSize="experienceLetterFileSize"
        :experienceLetterFileType="experienceLetterFileType"
        :salarySlipFileName="salarySlipFileName"
        :salarySlipFileSize="salarySlipFileSize"
        :salarySlipFileType="salarySlipFileType"
        :tempExperienceLetterUrl="tempExperienceLetterUrl"
        :tempSalarySlipUrl="tempSalarySlipUrl"
        :documentLoading="documentLoading"
        @submit-documents="submitVerificationDocuments"
        @file-selected="handleDocumentFileSelected"
      />
    </div>

    <div class="profile-card-container">
      <div class="profile-card">
        <div class="profile-header" style="border:1px solid #ccc" @click.stop>
          <div class="profile-image-wrapper">
            <img
              :src="profileImageSrc"
              alt="Profile Background" class="profile-background" style="width: 60%; height: 100%;" />
            <div class="image-overlay" @click.stop="triggerProfileFileInput">
              <i class="fas fa-camera update-icon" style="font-size: 25px; color: white"></i>
            </div>
            <input id="profileImage" type="file" ref="profileImageInput" @change="handleProfileImageUpload" accept="image/*" class="file-input" />
            <div v-if="uploadingProfileImage" class="loading-overlay">
              <div class="spinner"></div>
              <span>Uploading...</span>
            </div>
          </div>
        </div>
        <div class="profile-info">
          <h3 class="profile-name">{{ user.username || 'John Doe' }}</h3>
          <p class="profile-title">{{ getDisplayTitle(user.designation, user.grade) || '' }}</p>
          <div class="profile-details">
            <div class="profile-detail-item">
              <span class="detail-label">Email</span>
              <span class="detail-value">{{ user.email || 'employee@expertizo.pk' }}</span>
            </div>
            <div class="profile-detail-item">
              <span class="detail-label">Contact</span>
              <span class="detail-value">{{ user.phoneNumber || '+1234567890' }}</span>
            </div>
            <div class="profile-detail-item">
              <span class="detail-label">Nationality</span>
              <span class="detail-value">{{ user.nationality || 'Pakistan' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "../../../../assets/config/firebase";
import { uploadToCloudinary } from '../../../../utils/cloudinary';
import bankName from "../../../data/banks.json";
import countries from "../../../data/UserProfileData/countries.json";
import { designations, designationGrades } from './ProfileData';
import CustomDropdown from '../../../UIComponents/CustomDropdown.vue';
import GradeDropdown from '../../../UIComponents/GradeDropdown.vue';
import VerifyDocuments from './VerifyDocuments.vue';
import swal from "sweetalert";
import { validateForm } from '../../../../assets/config/helpers';
import { openFileInNewTab } from '../../../../utils/helperFunc';
import { formatValue, getUpdatedFields, logAdminActivity } from "../../../../utils/adminLogs";
import { handleActivityNotification } from "../../../../services/notificationService";

export default {
  components: {
    CustomDropdown,
    GradeDropdown,
    VerifyDocuments,
  },
  data: function() {
    return {
      profileLoading: false,
      documentLoading: false,
      user: null,
      errors: {},
      customBankName: '',
      showCustomBank: false,
      uploadingProfileImage: false,
      isUploadingExperienceLetter: false,
      isUploadingSalarySlip: false,
      uploadProgress: 0,
      profile_picture: '',
      pendingNicFile: null,
      nicLocalPreviewUrl: '',
      nicLocalFileType: '',
      nicFirebaseFileType: '',
      pendingExperienceLetterFile: null,
      experienceLetterLocalPreviewUrl: '',
      experienceLetterLocalFileType: '',
      experienceLetterFirebaseFileType: '',
      pendingSalarySlipFile: null,
      salarySlipLocalPreviewUrl: '',
      salarySlipLocalFileType: '',
      salarySlipFirebaseFileType: '',
      experienceLetterFileName: '',
      experienceLetterFileSize: '',
      experienceLetterFileType: '',
      salarySlipFileName: '',
      salarySlipFileSize: '',
      salarySlipFileType: '',
      designations,
      designationGrades,
      countries: countries.countries,
      banks: bankName.banks,
      maritalStatusOptions: ['Single', 'Married', 'Divorced', 'Widowed'],
      defaultProfileImage: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80',
      isLoading: true
    };
  },
  computed: {
    companyName() {
      const company = this.$store.state.auth.company;
      return (company && company.companyName) || '';
    },
    profileImageSrc: function() {
      return this.user && this.user.profile_picture ? this.user.profile_picture : this.defaultProfileImage;
    },
    isCurrentUser: function() {
      return !this.$route.params.id || this.$route.params.id === (this.$store.state.auth.user && this.$store.state.auth.user.uid);
    },
    isNonAdmin: function() {
      return this.$store.state.auth.user && this.$store.state.auth.user.role !== 'admin';
    },
    nicPreviewUrl: function() {
      return this.nicLocalPreviewUrl || (this.user && this.user.nicImageUrl) || '';
    },
    nicPreviewFileType: function() {
      return this.nicLocalFileType || this.nicFirebaseFileType || '';
    },
    nicPreviewIsImage: function() {
      return this.nicPreviewFileType && this.nicPreviewFileType.match('image.*');
    },
    nicPreviewIsPdf: function() {
      return this.nicPreviewFileType === 'application/pdf';
    },
    tempExperienceLetterUrl: function() {
      return this.experienceLetterLocalPreviewUrl || (this.user && this.user.certificateUrl) || '';
    },
    tempSalarySlipUrl: function() {
      return this.salarySlipLocalPreviewUrl || (this.user && this.user.salarySlipUrl) || '';
    },
  },
  created: async function() {
    var defaultUserData = {
      bankName: '',
      email: '',
      iban: '',
      isActive: true,
      joiningDate: '',
      leavesAllowed: 7.5,
      leavesAvailable: 15,
      leavesTaken: '',
      nicNumber: '',
      phoneNumber: '',
      role: '',
      salary: 0,
      twitterProfile: '',
      uid: this.$store.state.auth.user ? this.$store.state.auth.user.uid : '',
      username: '',
      designation: '',
      grade: '',
      role_title: '',
      personalEmail: '',
      dateOfBirth: '',
      nationality: '',
      maritalStatus: '',
      currentAddress: '',
      permanentAddress: '',
      emergencyContactName: '',
      emergencyContactNumber: '',
      profile_picture: '',
      nicImageUrl: '',
      certificateUrl: '',
      salarySlipUrl: '',
      company: this.companyName,
      fullName: '',
      status: 'Active' 
    };

    this.isLoading = true;
    var routeUserId = this.$route.params.id || (localStorage.getItem('impersonateUser') ? JSON.parse(localStorage.getItem('impersonateUser')).uid : null);

    try {
      await this.$store.dispatch('initializeAuth');
      var userData;
      if (routeUserId) {
        userData = await firebase.getSingleUser(routeUserId);
        if (!userData) {
          throw new Error('User not found');
        }
      } else {
        userData = this.$store.state.auth.user || defaultUserData;
      }

      this.user = {
        ...userData,
        bankName: userData.bankName || '',
        email: userData.email || '',
        iban: userData.iban || '',
        isActive: userData.isActive !== undefined ? userData.isActive : true,
        joiningDate: userData.joiningDate || '',
        leavesAllowed: userData.leavesAllowed || 7.5,
        leavesAvailable: userData.leavesAvailable || 15,
        leavesTaken: userData.leavesTaken || '',
        nicNumber: userData.nicNumber || '',
        phoneNumber: userData.phoneNumber || '',
        role: userData.role || '',
        salary: userData.salary || 0,
        twitterProfile: userData.twitterProfile || '',
        uid: routeUserId || userData.uid || (this.$store.state.auth.user ? this.$store.state.auth.user.uid : ''),
        username: userData.username || '',
        designation: userData.designation || '',
        grade: userData.grade || '',
        role_title: userData.role_title || '',
        personalEmail: userData.personalEmail || '',
        dateOfBirth: userData.dateOfBirth || '',
        nationality: userData.nationality || '',
        maritalStatus: userData.maritalStatus || '',
        currentAddress: userData.currentAddress || '',
        permanentAddress: userData.permanentAddress || '',
        emergencyContactName: userData.emergencyContactName || '',
        emergencyContactNumber: userData.emergencyContactNumber || '',
        profile_picture: userData.profile_picture || '',
        nicImageUrl: userData.nicImageUrl || '',
        certificateUrl: userData.certificateUrl || '',
        salarySlipUrl: userData.salarySlipUrl || '',
        company: this.companyName,
        fullName: userData.username || '',
        status: userData.status || (this.$store.state.auth.user && this.$store.state.auth.user.status) || 'Active'
      };

      if (this.user.nicImageUrl) {
        this.nicFirebaseFileType = this.inferFileType(this.user.nicImageUrl);
      }
      if (this.user.certificateUrl) {
        this.experienceLetterFirebaseFileType = this.inferFileType(this.user.certificateUrl);
        this.experienceLetterFileType = this.experienceLetterFirebaseFileType;
      }
      if (this.user.salarySlipUrl) {
        this.salarySlipFirebaseFileType = this.inferFileType(this.user.salarySlipUrl);
        this.salarySlipFileType = this.salarySlipFirebaseFileType;
      }

      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    } catch (error) {
      console.error('Error fetching user from Firebase:', error);
      swal('Error', 'Failed to load user data: ' + error.message, 'error');
      this.user = defaultUserData;
      this.$router.push('/userManagement');
    } finally {
      this.isLoading = false;
    }
  },
  mounted: function() {
    if (this.user && this.user.profile_picture) {
      this.profile_picture = this.user.profile_picture;
    }
    if (!this.user.joiningDate) {
      var today = new Date();
      var formattedDate = today.toISOString().split('T')[0];
      this.user.joiningDate = formattedDate;
    }
  },
  methods: {
    openFileInNewTab,
    initBankField() {
      if (
        this.user.bankName &&
        !this.banks.includes(this.user.bankName)
      ) {
        this.showCustomBank = true;
        this.customBankName = this.user.bankName;
      }
    },
    inferFileType(url) {
      if (!url) return '';
      const extension = url.split('.').pop().toLowerCase().split('?')[0];
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image/' + extension;
      if (extension === 'pdf') return 'application/pdf';
      return '';
    },
    onDesignationChange: function(value) {
      this.user.designation = value;
      this.user.grade = '';
      this.user.role_title = '';
      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    },
    onGradeChange: function(value) {
      this.user.grade = value;
      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    },
    onBankChange: function(value) {
      if (!value) return;

      this.showCustomBank = value === 'Other';

      if (this.showCustomBank) {
        this.$nextTick(() => this.$refs.customBank.focus());

        if (this.customBankName) {
          this.user.bankName = this.customBankName;
        }
      } else {
        this.customBankName = '';
        this.user.bankName = value;
      }
      if (value) this.errors.bankName = undefined;
      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    },
    onCustomBankInput: function () {
      if (!this.customBankName) return;
      this.user.bankName = this.customBankName;
      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    },
    onNationalityChange: function(value) {
      this.user.nationality = value;
      if (value) this.errors.nationality = undefined;
      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    },
    onMaritalStatusChange: function(value) {
      this.user.maritalStatus = value;
      if (value) this.errors.maritalStatus = undefined;
      if (this.isCurrentUser) {
        this.$store.commit('setUser', this.user);
      }
    },
    getGradesForDesignation: function(designation) {
      return this.designationGrades[designation] || [];
    },
    getDisplayTitle: function (designation, grade) {
      if (!designation || !grade) return '';
      var grades = this.designationGrades[designation] || [];
      var gradeObj = grades.find(function (g) { return g.value.toString() === grade.toString(); });
      if (!gradeObj) return '';

      var match = gradeObj.label.match(/\{([^}]+)\}/);
      var extractedTitle = match ? match[1] : gradeObj.label;

      var gradeNumberMatch = gradeObj.label.match(/(Grade\s*\d+)/i);
      var gradeNumber = gradeNumberMatch ? gradeNumberMatch[1] : '';

      return  extractedTitle;
    },
    getRoleTitle: function (designation, grade) {
      if (!designation || !grade) return '';
      var grades = this.designationGrades[designation] || [];
      var gradeObj = grades.find(function (g) { return g.value.toString() === grade.toString(); });
      if (!gradeObj) return '';
      var match = gradeObj.label.match(/\{([^}]+)\}/);
      return match ? match[1] : gradeObj.label;
    },
    validateForm: function() {
      const { isValid, errors } = validateForm({
        ...this.user,
        bankName: this.showCustomBank ? this.customBankName : this.user.bankName,
        nicImageUrl: this.pendingNicFile ? 'pending' : (this.user.nicImageUrl || ''),
      }, this.isNonAdmin);
      this.errors = errors;
      return isValid;
    },
    updateProfile: async function() {
      if (this.validateForm()) {
        this.profileLoading = true;
        try {
          const oldUserData = await firebase.getSingleUser(this.user.uid);
          const loggedInUser = this.$store.state.auth.user;
          const targetUser = this.user;
          const oldNicUrl = this.user.nicImageUrl || '';
          let finalNicImageUrl = oldNicUrl;
          if (this.pendingNicFile) {
            finalNicImageUrl = await uploadToCloudinary(this.pendingNicFile, 'user_cnic');
            const actionType = oldNicUrl ? 'replaced' : 'uploaded';
            await this.logDocumentActivity('CNIC', actionType, targetUser, loggedInUser, oldNicUrl, finalNicImageUrl);
          }

          const userDataToUpdate = {
            username: this.user.fullName,
            designation: this.user.designation,
            grade: this.user.grade,
            companyEmail: this.user.email,
            personalEmail: this.user.personalEmail,
            nicNumber: this.user.nicNumber,
            dateOfBirth: this.user.dateOfBirth,
            joiningDate: this.user.joiningDate,
            bankName: this.showCustomBank ? this.customBankName : this.user.bankName,
            iban: this.user.iban,
            nationality: this.user.nationality,
            maritalStatus: this.user.maritalStatus,
            phoneNumber: this.user.phoneNumber,
            currentAddress: this.user.currentAddress,
            permanentAddress: this.user.permanentAddress,
            emergencyContactName: this.user.emergencyContactName,
            emergencyContactNumber: this.user.emergencyContactNumber,
            nicImageUrl: finalNicImageUrl,
          };

          if (!this.user.uid) {
            throw new Error('User UID is undefined');
          }
          await firebase.updateEmployee(userDataToUpdate, this.user.uid);
          this.user = Object.assign({}, this.user, userDataToUpdate, { status: this.user.status });

          if (this.pendingNicFile) {
            this.user.nicImageUrl = finalNicImageUrl;
            this.nicFirebaseFileType = this.nicLocalFileType;
            URL.revokeObjectURL(this.nicLocalPreviewUrl);
            this.nicLocalPreviewUrl = '';
            this.nicLocalFileType = '';
            this.pendingNicFile = null;
          }

          const updatedFields = getUpdatedFields(oldUserData, userDataToUpdate);
          
          const isAdmin = loggedInUser.role === 'admin';
          const isUpdatingOwnProfile = loggedInUser.uid === targetUser.uid;

          let activityName = '';
          let activityDescription = '';
          let changes = '';

          if (isUpdatingOwnProfile) {
            activityName = `${loggedInUser.username} updated their profile details`;
            activityDescription = `${loggedInUser.email} updated their profile details.`;
          } 
          else {
            activityName = `${loggedInUser.username} updated ${targetUser.username}'s profile details`;
            activityDescription = `${loggedInUser.email} updated ${targetUser.email}'s profile details.`;
          }
          changes = Object.entries(updatedFields)
            .map(([key, value]) => `${key}: ${formatValue(value.old)} → ${formatValue(value.new)}`)
            .join('\n');
          const adminActivity = {
            activityOnUserName: targetUser.username,
            activityByUserName: loggedInUser.username,
            activityName,
            activityOnUser: targetUser.uid,
            activityTitle: `Profile updated`,
            activityDescription,
            changes: changes,
            type: 'profile',
            timestamp: new Date().toLocaleString('en-PK'),
          };

          if (isAdmin) {
            adminActivity.adminId = loggedInUser.uid;
          }

          await logAdminActivity(adminActivity);

          const { uid: adminId, username: adminUserName } = this.$store.state.auth.user;
          await handleActivityNotification(
            {
              type: "update_user",
              activityDescription: activityDescription,
              activityOnUser: targetUser.uid,
              activityOnUserName: targetUser.username,
              activityByUserName: adminUserName,
              activityName: activityName,
              isAdmin: isAdmin,
            },
            this.$store.state.auth,
            adminId
          );

          if (this.isCurrentUser) {
            this.$store.commit('setUser', this.user);
          }
          this.$emit('update-profile', this.user);
          this.profileLoading = false;
          swal('Success', 'Profile updated successfully', 'success');
        } catch (error) {
          swal('Error', 'Failed to update profile: ' + error.message, 'error');
          this.profileLoading = false;
        }
      } else {
        this.$nextTick(function () {
          const firstError = document.querySelector('.is-invalid,.invalid-feedback');
          if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    },
    submitVerificationDocuments: async function() {
      try {
        this.documentLoading = true;
        const loggedInUser = this.$store.state.auth.user;
        const targetUser = this.user;
        const oldCertUrl = this.user.certificateUrl || '';
        const oldSlipUrl = this.user.salarySlipUrl || '';
        let finalCertificateUrl = oldCertUrl;
        let finalSalarySlipUrl = oldSlipUrl;
        if (this.pendingExperienceLetterFile) {
          finalCertificateUrl = await uploadToCloudinary(this.pendingExperienceLetterFile, 'experience_letter');
          const actionType = oldCertUrl ? 'replaced' : 'uploaded';
          await this.logDocumentActivity('Academic Certificate/Experience Letter', actionType, targetUser, loggedInUser, oldCertUrl, finalCertificateUrl);
        }
        
        if (this.pendingSalarySlipFile) {
          finalSalarySlipUrl = await uploadToCloudinary(this.pendingSalarySlipFile, 'salary_slip');
          const actionType = oldSlipUrl ? 'replaced' : 'uploaded';
          await this.logDocumentActivity('Salary Slip', actionType, targetUser, loggedInUser, oldSlipUrl, finalSalarySlipUrl);
        }

        const documentsData = {
          certificateUrl: finalCertificateUrl,
          salarySlipUrl: finalSalarySlipUrl
        };
        if (!this.user.uid) {
          throw new Error('User UID is undefined');
        }
        await firebase.updateEmployee(documentsData, this.user.uid);
        this.user = Object.assign({}, this.user, documentsData);
        if (this.pendingExperienceLetterFile) {
          this.user.certificateUrl = finalCertificateUrl;
          this.experienceLetterFirebaseFileType = this.experienceLetterLocalFileType;
          URL.revokeObjectURL(this.experienceLetterLocalPreviewUrl);
          this.experienceLetterLocalPreviewUrl = '';
          this.experienceLetterLocalFileType = '';
          this.pendingExperienceLetterFile = null;
        }
        if (this.pendingSalarySlipFile) {
          this.user.salarySlipUrl = finalSalarySlipUrl;
          this.salarySlipFirebaseFileType = this.salarySlipLocalFileType;
          URL.revokeObjectURL(this.salarySlipLocalPreviewUrl);
          this.salarySlipLocalPreviewUrl = '';
          this.salarySlipLocalFileType = '';
          this.pendingSalarySlipFile = null;
        }

        if (this.isCurrentUser) {
          this.$store.commit('setUser', this.user);
        }
        this.documentLoading = false;
        this.$emit('submit-documents', documentsData);
        swal('Success', 'Verification documents submitted successfully', 'success');
      } catch (error) {
        this.documentLoading = false;
        swal('Error', 'Failed to submit verification documents: ' + error.message, 'error');
      }
    },
    triggerNicFileInput: function() {
      this.$refs.nicImageInput.click();
    },
    handleNicFileDrop: function(event) {
      const file = event.dataTransfer.files[0];
      if (file) this.selectNicFile(file);
    },
    handleNicImageUpload: function(event) {
      const file = event.target.files[0];
      if (file) this.selectNicFile(file);
      event.target.value = '';
    },
    selectNicFile: function(file) {
      if (!file.type.match('image.*|application/pdf')) {
        swal('Error', 'Please select an image or PDF file for NIC Picture', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        swal('Error', 'File size should not exceed 5MB', 'error');
        return;
      }
      if (this.nicLocalPreviewUrl && this.nicLocalPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.nicLocalPreviewUrl);
      }
      this.pendingNicFile = file;
      this.nicLocalPreviewUrl = URL.createObjectURL(file);
      this.nicLocalFileType = file.type;
      this.errors.nicImageUrl = null;
    },
    handleDocumentFileSelected: function(file, type) {
      if (type === 'experience_letter') {
        this.selectExperienceLetterFile(file);
      } else if (type === 'salary_slip') {
        this.selectSalarySlipFile(file);
      }
    },
    selectExperienceLetterFile: function(file) {
      if (!file.type.match('image.*|application/pdf')) {
        swal('Error', 'Please select a valid file format for Academic Certificate/Experience Letter', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        swal('Error', 'File size should not exceed 5MB', 'error');
        return;
      }
      if (this.experienceLetterLocalPreviewUrl && this.experienceLetterLocalPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.experienceLetterLocalPreviewUrl);
      }
      this.pendingExperienceLetterFile = file;
      this.experienceLetterLocalPreviewUrl = URL.createObjectURL(file);
      this.experienceLetterLocalFileType = file.type;
      this.experienceLetterFileName = file.name;
      this.experienceLetterFileSize = this.formatFileSize(file.size);
      this.experienceLetterFileType = file.type;
    },
    selectSalarySlipFile: function(file) {
      if (!file.type.match('image.*|application/pdf')) {
        swal('Error', 'Please select a valid file format for Salary Slip', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        swal('Error', 'File size should not exceed 5MB', 'error');
        return;
      }
      if (this.salarySlipLocalPreviewUrl && this.salarySlipLocalPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.salarySlipLocalPreviewUrl);
      }
      this.pendingSalarySlipFile = file;
      this.salarySlipLocalPreviewUrl = URL.createObjectURL(file);
      this.salarySlipLocalFileType = file.type;
      this.salarySlipFileName = file.name;
      this.salarySlipFileSize = this.formatFileSize(file.size);
      this.salarySlipFileType = file.type;
    },

    triggerProfileFileInput: function() {
      if (this.$refs.profileImageInput) {
        this.$refs.profileImageInput.click();
      } else {
        console.error('profileImageInput ref is undefined');
      }
    },
    handleProfileImageUpload: async function(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!file.type.match('image.*')) {
        swal('Error', 'Please select an image file for Profile Picture', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        swal('Error', 'File size should not exceed 5MB', 'error');
        return;
      }
      try {
        this.uploadingProfileImage = true;
        const url = await uploadToCloudinary(file, 'profile_images');
        this.user.profile_picture = url;
        this.errors.profile_picture = null;
        await firebase.updateEmployee({ profile_picture: url }, this.user.uid);
        if (this.isCurrentUser) {
          this.$store.commit('setUser', this.user);
        }
      } catch (error) {
        swal('Error', 'Failed to upload profile image: ' + error.message, 'error');
      } finally {
        this.uploadingProfileImage = false;
      }
    },
    formatFileSize: function(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
     getDocumentThumbnail(url) {
      if (!url || !url.includes('cloudinary.com')) return url;
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.endsWith('.pdf')) {
        let normalized = url.replace('/raw/upload/', '/image/upload/').replace('/raw/', '/image/');
        const parts = normalized.split('/upload/');
        if (parts.length !== 2) return url;
        const transform = 'w_150,h_200,c_thumb,pg_1,f_auto,q_auto';
        return `${parts[0]}/upload/${transform}/${parts[1]}`;
      }

      return url;
    },

    logDocumentActivity: async function(documentType, actionType, targetUser, loggedInUser, oldUrl, newUrl) {
      const isUpdatingOwnProfile = loggedInUser.uid === targetUser.uid;

      let activityName = '';
      let activityDescription = '';

      if (isUpdatingOwnProfile) {
        activityName = `${loggedInUser.username} ${actionType} their ${documentType}`;
        activityDescription = `${loggedInUser.email} ${actionType} their ${documentType}.`;
      } else {
        activityName = `${loggedInUser.username} ${actionType} ${documentType} for ${targetUser.username}`;
        activityDescription = `${loggedInUser.email} ${actionType} ${documentType} for ${targetUser.email}.`;
      }
      let changesLine = '';
      if (oldUrl && newUrl) {
        changesLine = `${documentType}: ${oldUrl} → ${newUrl}`;
      } else if (newUrl) {
        changesLine = `${documentType}: ${newUrl}`;
      } else {
        changesLine = `${documentType}: ${actionType} at ${new Date().toLocaleString('en-PK')}`;
      }

      const adminActivity = {
        activityOnUserName: targetUser.username,
        activityByUserName: loggedInUser.username,
        activityName,
        activityOnUser: targetUser.uid,
        activityTitle: `Document ${actionType}`,
        activityDescription,
        changes: changesLine,
        type: 'document',
        documentType,
        actionType,
        timestamp: new Date().toLocaleString('en-PK'),
      };
      if (loggedInUser.role === 'admin') {
        adminActivity.adminId = loggedInUser.uid;
      }
      await logAdminActivity(adminActivity);
      const { uid: adminId } = this.$store.state.auth.user;
      if (documentType !== 'CNIC') {
        await handleActivityNotification(
          {
            type: 'update_user',
            activityDescription,
            activityOnUser: targetUser.uid,
            activityOnUserName: targetUser.username,
            activityByUserName: loggedInUser.username,
            activityName,
            isAdmin: loggedInUser.role === 'admin',
          },
          this.$store.state.auth,
          adminId
        );
      }
    },
  },
  beforeDestroy: function() {
    if (this.nicLocalPreviewUrl && this.nicLocalPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.nicLocalPreviewUrl);
    }
    if (this.experienceLetterLocalPreviewUrl && this.experienceLetterLocalPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.experienceLetterLocalPreviewUrl);
    }
    if (this.salarySlipLocalPreviewUrl && this.salarySlipLocalPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.salarySlipLocalPreviewUrl);
    }
  },
  watch: {
    'user.bankName': function () {
      this.initBankField();
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/UserProfile/ProfileForm.vue